import { Controller } from "@hotwired/stimulus"

/**
 * Back to Top Button Controller
 *
 * Shows/hides a floating "Back to Top" button based on scroll position.
 * Provides smooth scroll animation to page top.
 *
 * Features:
 * - Auto-show after scrolling 300px (configurable)
 * - Smooth scroll animation
 * - Fade in/out transition
 * - Mobile-friendly positioning
 * - Keyboard accessible
 *
 * Usage:
 *   <button
 *     data-controller="back-to-top"
 *     data-action="click->back-to-top#scrollToTop"
 *     data-back-to-top-threshold-value="300"
 *     class="fixed bottom-6 right-6 hidden"
 *   >
 *     ↑ Top
 *   </button>
 *
 * @class BackToTopController
 * @extends Controller
 */
export default class extends Controller {
  static values = {
    threshold: { type: Number, default: 300 } // Show button after scrolling this many pixels
  }

  /**
   * Initialize scroll listener on controller connect
   */
  connect() {
    console.log("Back to Top button connected")

    // Bind scroll handler
    this.scrollHandler = this.handleScroll.bind(this)

    // Add scroll event listener with passive flag for better performance
    window.addEventListener("scroll", this.scrollHandler, { passive: true })

    // Initial check
    this.handleScroll()
  }

  /**
   * Cleanup: Remove scroll listener on disconnect
   */
  disconnect() {
    window.removeEventListener("scroll", this.scrollHandler)
  }

  /**
   * Handle scroll event - show/hide button based on scroll position
   */
  handleScroll() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop

    if (scrollPosition > this.thresholdValue) {
      this.showButton()
    } else {
      this.hideButton()
    }
  }

  /**
   * Show the "Back to Top" button with fade-in animation
   */
  showButton() {
    if (this.element.classList.contains("hidden")) {
      this.element.classList.remove("hidden")

      // Force reflow for animation
      void this.element.offsetWidth

      // Add visible class for fade-in
      this.element.classList.add("opacity-100")
      this.element.classList.remove("opacity-0")

      // Update ARIA for screen readers
      this.element.setAttribute("aria-hidden", "false")
    }
  }

  /**
   * Hide the "Back to Top" button with fade-out animation
   */
  hideButton() {
    if (!this.element.classList.contains("hidden")) {
      this.element.classList.add("opacity-0")
      this.element.classList.remove("opacity-100")

      // Hide completely after transition (300ms)
      setTimeout(() => {
        this.element.classList.add("hidden")
        this.element.setAttribute("aria-hidden", "true")
      }, 300)
    }
  }

  /**
   * Scroll to top of page with smooth animation
   * @param {Event} event - Click event
   */
  scrollToTop(event) {
    event.preventDefault()

    // Smooth scroll to top
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    })

    // Focus on main content for accessibility (skip links)
    const mainContent = document.querySelector("main") || document.querySelector("h1")
    if (mainContent) {
      mainContent.setAttribute("tabindex", "-1")
      mainContent.focus()
    }

    // Optional: Announce to screen readers
    this.announceToScreenReader(
      this.element.dataset.announceText || "Scrolled to top of page"
    )
  }

  /**
   * Announce message to screen readers using live region
   * @param {string} message - Message to announce
   */
  announceToScreenReader(message) {
    const liveRegion = document.createElement("div")
    liveRegion.setAttribute("role", "status")
    liveRegion.setAttribute("aria-live", "polite")
    liveRegion.classList.add("sr-only") // Screen reader only class
    liveRegion.textContent = message

    document.body.appendChild(liveRegion)

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(liveRegion)
    }, 1000)
  }
}
